import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostBinding, inject, input, output, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TileComponent } from "../../tile/tile.component";
import { SketchToolEnum } from "../../../core-ui.enums";
import { SketchService, StreamState } from "../../../services";
import { PointerService } from "../../../services/pointer.service";
import { IStream } from "@auvious/rtc";

@Component({
  selector: "app-sketch-controls",
  templateUrl: "./sketch-controls.component.html",
  styleUrls: ["./sketch-controls.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SketchControlsComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private sketch = inject(SketchService);
  private point = inject(PointerService);
  private cd = inject(ChangeDetectorRef);

  readonly room = input<string>(undefined);
  readonly stream = input<StreamState>(undefined);
  readonly column = input<boolean>(undefined);
  readonly disabled = input<boolean>(undefined);

  readonly controlStateChanged = output<boolean>();

  activeTool: SketchToolEnum;
  confirmClear = false;
  confirmPosition = "top-left";

  constructor() {
    const tile = inject(TileComponent, { optional: true });

    this.confirmPosition = !!tile ? "top-right" : "top-left";
  }

  @HostBinding("class") get class() {
    return {
      column: this.column(),
    };
  }

  ngOnInit(): void {
    this.sketch.toolSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tool) => {
      this.activeTool = tool;
      this.cd.detectChanges();
    });
    this.point.zoomFrameChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data.on && this.activeTool) {
        switch (this.activeTool) {
          case SketchToolEnum.arrow:
            this.arrow();
            break;
          case SketchToolEnum.marker:
            this.marker();
            break;
          case SketchToolEnum.eraser:
            this.eraser();
            break;
        }
      }
      this.cd.detectChanges();
    });
  }

  marker() {
    this.tryStopZoom();
    this.sketch.marker(!this.markerOn, this.room());
    this.controlStateChanged.emit(this.markerOn);
  }

  arrow() {
    this.tryStopZoom();
    this.sketch.arrow(!this.arrowOn, this.room());
    this.controlStateChanged.emit(this.arrowOn);
  }

  eraser() {
    this.tryStopZoom();
    this.sketch.eraser(!this.eraserOn, this.room());
    this.controlStateChanged.emit(this.eraserOn);
  }

  clear() {
    this.sketch.clear(this.room());
    this.confirmClear = false;
  }

  tryStopZoom() {
    const stream = this.stream();
    if (this.zoomOn && !!stream) {
      this.point.zoomFrame(stream.originator, stream.correlationId);
    }
  }

  get tooltipPosition() {
    return this.column() ? "left" : "bottom";
  }

  get isDisabled() {
    return !this.room();
  }

  get markerOn() {
    return this.activeTool === SketchToolEnum.marker;
  }

  get eraserOn() {
    return this.activeTool === SketchToolEnum.eraser;
  }

  get arrowOn() {
    return this.activeTool === SketchToolEnum.arrow;
  }

  get zoomOn() {
    return this.activeTool === SketchToolEnum.magnify;
  }
}
